import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { Target, ChevronRight, Check, Play, Bell, BellOff } from 'lucide-react'
import { Card, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL + '/api'

export default function KaizenWidget() {
  const navigate = useNavigate()
  const { t, locale } = useLanguage()
  
  const [todayPlan, setTodayPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notificationsEnabled, setNotificationsEnabled] = useState(true)
  
  const fetchTodayPlan = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kaizen/plans/today`)
      setTodayPlan(response.data)
    } catch (error) {
      console.error('Error fetching kaizen plan:', error)
    } finally {
      setLoading(false)
    }
  }, [])
  
  const fetchNotificationSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kaizen/notification-settings`)
      setNotificationsEnabled(response.data.kaizen_push_enabled)
    } catch (error) {
      console.error('Error fetching notification settings:', error)
    }
  }, [])
  
  useEffect(() => {
    fetchTodayPlan()
    fetchNotificationSettings()
  }, [fetchTodayPlan, fetchNotificationSettings])
  
  const toggleNotifications = async (e) => {
    e.stopPropagation()
    const newEnabled = !notificationsEnabled
    
    if (newEnabled && 'Notification' in window) {
      if (Notification.permission === 'denied') {
        toast.error(locale === 'es-MX' 
          ? 'Notificaciones bloqueadas. Habilítalas desde la configuración.'
          : 'Notifications blocked. Enable them from settings.')
        return
      }
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission()
        if (permission !== 'granted') return
      }
    }
    
    try {
      await axios.put(`${API}/kaizen/notification-settings`, {
        kaizen_push_enabled: newEnabled
      })
      setNotificationsEnabled(newEnabled)
      toast.success(newEnabled ? t('kaizen.notificationsEnabled') : t('kaizen.notificationsDisabled'))
    } catch (error) {
      console.error('Error toggling notifications:', error)
    }
  }
  
  const updateItemStatus = async (e, itemId, newStatus) => {
    e.stopPropagation()
    if (!todayPlan) return
    
    try {
      await axios.put(`${API}/kaizen/plans/${todayPlan.id}/items/${itemId}`, {
        status: newStatus
      })
      fetchTodayPlan()
    } catch (error) {
      console.error('Error updating item:', error)
    }
  }
  
  if (loading) {
    return null
  }
  
  // Get progress
  const getProgress = () => {
    if (!todayPlan?.items?.length) return { done: 0, total: 0 }
    const done = todayPlan.items.filter(i => i.status === 'done' || i.status === 'partial').length
    return { done, total: todayPlan.items.length }
  }
  
  // Get next pending item
  const getNextPending = () => {
    if (!todayPlan?.items) return null
    return todayPlan.items.find(i => i.status === 'pending' || i.status === 'in_progress')
  }
  
  const progress = getProgress()
  const nextPending = getNextPending()
  
  // Get status badge color
  const getStatusColor = () => {
    if (!todayPlan) return 'bg-secondary'
    if (todayPlan.status_summary === 'done') return 'bg-emerald-500 text-white'
    if (todayPlan.status_summary === 'in_progress') return 'bg-blue-500 text-white'
    if (todayPlan.status_summary === 'partial') return 'bg-yellow-500 text-white'
    return 'bg-secondary'
  }
  
  return (
    <Card 
      className="cursor-pointer hover:bg-accent/50 transition-colors border-primary/20"
      onClick={() => navigate('/kaizen')}
      data-testid="kaizen-widget"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-primary" />
            <span className="font-semibold text-sm">{t('kaizen.planToday')}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={toggleNotifications}
              className="p-1 hover:bg-secondary rounded-full transition-colors"
              data-testid="kaizen-widget-bell"
            >
              {notificationsEnabled ? (
                <Bell className="w-4 h-4 text-primary" />
              ) : (
                <BellOff className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            
            {todayPlan && (
              <Badge className={`text-xs ${getStatusColor()}`}>
                {progress.done}/{progress.total}
              </Badge>
            )}
          </div>
        </div>
        
        {todayPlan ? (
          <div className="space-y-2">
            {/* Category */}
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              {todayPlan.category_name}
              {todayPlan.subcategory_name && (
                <>
                  <ChevronRight className="w-3 h-3" />
                  {todayPlan.subcategory_name}
                </>
              )}
            </p>
            
            {/* Next pending item */}
            {nextPending ? (
              <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{nextPending.title}</p>
                  {nextPending.minimum_version && (
                    <p className="text-xs text-muted-foreground">Min: {nextPending.minimum_version}</p>
                  )}
                </div>
                
                <div className="flex gap-1">
                  {nextPending.status === 'pending' && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-blue-500"
                      onClick={(e) => updateItemStatus(e, nextPending.id, 'in_progress')}
                    >
                      <Play className="w-3 h-3" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-emerald-500"
                    onClick={(e) => updateItemStatus(e, nextPending.id, 'done')}
                  >
                    <Check className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ) : todayPlan.day_closed_at ? (
              <p className="text-xs text-emerald-600 flex items-center gap-1">
                <Check className="w-3 h-3" />
                {t('kaizen.dayClosed')}
              </p>
            ) : (
              <p className="text-xs text-emerald-600 flex items-center gap-1">
                <Check className="w-3 h-3" />
                {locale === 'es-MX' ? '¡Todas completadas!' : 'All completed!'}
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{t('kaizen.noPlanYet')}</p>
            <Button 
              size="sm" 
              variant="outline" 
              className="rounded-full h-7 text-xs"
              onClick={(e) => { e.stopPropagation(); navigate('/kaizen') }}
            >
              {t('kaizen.createPlan')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
